import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Calendar } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const services = [
  "General Psychiatry Consultation",
  "Deaddiction Programme",
  "Sex Therapy Clinic",
  "Interventional Neuromodulation (rTMS)",
  "Couples / Family Therapy",
  "Adolescent & Child Psychiatry",
  "Other",
];

const timeSlots = [
  "09:30 AM", "10:30 AM", "11:30 AM", "12:30 PM",
  "03:30 PM", "04:30 PM", "05:30 PM", "06:30 PM", "07:30 PM",
];

export default function AppointmentDialog({ open, onOpenChange }) {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    service: "",
    preferred_date: "",
    preferred_time: "",
    message: "",
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.service || !form.preferred_date || !form.preferred_time) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/appointments", form);
      setSuccess(true);
      toast.success("Appointment request received");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSuccess(false);
      setForm({
        full_name: "", phone: "", email: "", service: "",
        preferred_date: "", preferred_time: "", message: "",
      });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="appointment-dialog"
        className="max-w-2xl bg-[#FDFBF7] border-[#A39171]/25 rounded-3xl p-8 md:p-10 max-h-[92vh] overflow-y-auto"
      >
        {success ? (
          <div data-testid="appointment-success" className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-[#E2C044]/20 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-[#2D2623]" />
            </div>
            <h3 className="font-display text-3xl text-[#2D2623] mb-3">Request Received</h3>
            <p className="text-[#5C524E] max-w-md mx-auto mb-6">
              Thank you. Our care coordinator will call you within the next few hours to confirm
              your appointment.
            </p>
            <Button
              data-testid="appointment-success-close"
              onClick={close}
              className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-11"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="text-left">
              <p className="eyebrow text-[#A39171]">Book a Visit</p>
              <DialogTitle className="font-display text-3xl md:text-4xl text-[#2D2623] tracking-tight">
                Schedule Your Consultation
              </DialogTitle>
              <DialogDescription className="text-[#5C524E] text-base">
                Confidential. Compassionate. A care coordinator will confirm by phone.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
              <div className="md:col-span-2">
                <Label className="text-[#5C524E] mb-2 block text-sm">Full Name *</Label>
                <Input
                  data-testid="appt-full-name"
                  value={form.full_name}
                  onChange={(e) => setField("full_name", e.target.value)}
                  className="bg-white/70 border-[#A39171]/30 rounded-xl h-12 focus-visible:ring-[#E07A5F]/50"
                  placeholder="Your name"
                />
              </div>

              <div>
                <Label className="text-[#5C524E] mb-2 block text-sm">Phone *</Label>
                <Input
                  data-testid="appt-phone"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  className="bg-white/70 border-[#A39171]/30 rounded-xl h-12 focus-visible:ring-[#E07A5F]/50"
                  placeholder="+91 ..."
                />
              </div>

              <div>
                <Label className="text-[#5C524E] mb-2 block text-sm">Email</Label>
                <Input
                  data-testid="appt-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  className="bg-white/70 border-[#A39171]/30 rounded-xl h-12 focus-visible:ring-[#E07A5F]/50"
                  placeholder="optional"
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-[#5C524E] mb-2 block text-sm">Service *</Label>
                <Select value={form.service} onValueChange={(v) => setField("service", v)}>
                  <SelectTrigger
                    data-testid="appt-service"
                    className="bg-white/70 border-[#A39171]/30 rounded-xl h-12 focus:ring-[#E07A5F]/50"
                  >
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FDFBF7] border-[#A39171]/25 rounded-2xl">
                    {services.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`appt-service-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[#5C524E] mb-2 block text-sm">Preferred Date *</Label>
                <Input
                  data-testid="appt-date"
                  type="date"
                  min={today}
                  value={form.preferred_date}
                  onChange={(e) => setField("preferred_date", e.target.value)}
                  className="bg-white/70 border-[#A39171]/30 rounded-xl h-12 focus-visible:ring-[#E07A5F]/50"
                />
              </div>

              <div>
                <Label className="text-[#5C524E] mb-2 block text-sm">Preferred Time *</Label>
                <Select value={form.preferred_time} onValueChange={(v) => setField("preferred_time", v)}>
                  <SelectTrigger
                    data-testid="appt-time"
                    className="bg-white/70 border-[#A39171]/30 rounded-xl h-12 focus:ring-[#E07A5F]/50"
                  >
                    <SelectValue placeholder="Choose a slot" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FDFBF7] border-[#A39171]/25 rounded-2xl max-h-64">
                    {timeSlots.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`appt-time-${t}`}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label className="text-[#5C524E] mb-2 block text-sm">Briefly describe your concern</Label>
                <Textarea
                  data-testid="appt-message"
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value)}
                  className="bg-white/70 border-[#A39171]/30 rounded-xl focus-visible:ring-[#E07A5F]/50 min-h-[100px]"
                  placeholder="Anything our team should know (optional)"
                />
              </div>

              <div className="md:col-span-2 mt-2 flex flex-col sm:flex-row gap-3 items-center justify-between">
                <p className="text-xs text-[#A39171] flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Your information is confidential and shared only with our clinical team.
                </p>
                <Button
                  data-testid="appt-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-12 font-medium w-full sm:w-auto"
                >
                  {submitting ? "Submitting…" : "Request Appointment"}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
